import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core"
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import type { LibraryItem } from "@/components/library/library-item"
import { flowAttachPicks } from "@/components/flow/flow-reference-adder"
import {
  FlowSlotAttachButtons,
  FlowSlotCard,
  FlowSlotEmptyHint,
  FlowSlotFrame,
  FlowSlotHeading,
  flowSlotSourceLabel,
  flowSlotTile,
  flowSlotViewUrl,
} from "@/components/flow/flow-slot-chrome"
import { useTranslation } from "@/hooks/use-translation"
import { libraryPickResult } from "@/hooks/use-library-pick-selection"
import type { FlowAttachOutcome, FlowAttachRequest } from "@/lib/flow-attach-input"
import { flowSlotDrop, withSlotOverride, type FlowSlotMove } from "@/lib/flow-slot-drag"
import { REFERENCE_IMAGE_FILE_ACCEPT, REFERENCE_VIDEO_FILE_ACCEPT } from "@/lib/reference-file-policy"
import { cn } from "@/lib/utils"
import type { ShotFrameSource } from "@/lib/workspace-api"
import type { FlowChainSlot, FlowNodeInput, FlowNodeRow } from "@/types/flow"

const EDGE_ID = "edge:"
const SLOT_ID = "slot:"

type SlotDrag = { edgeId: number; from: number; to: number | null; dy: number }

type Preview = { url: string; video: boolean; title: string }

function dragSuffix(id: UniqueIdentifier, prefix: string): number | null {
  const raw = String(id)
  if (!raw.startsWith(prefix)) return null
  const value = Number(raw.slice(prefix.length))
  return Number.isInteger(value) ? value : null
}

function DraggableSlotCard({
  edgeId,
  disabled,
  shiftY,
  ...card
}: {
  edgeId: number
  disabled: boolean
  shiftY: number | null
} & Omit<Parameters<typeof FlowSlotCard>[0], "cardRef" | "dragAttributes" | "dragListeners" | "className" | "style">) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${EDGE_ID}${edgeId}`,
    disabled,
    attributes: { role: "group", tabIndex: -1 },
  })
  return (
    <FlowSlotCard
      {...card}
      cardRef={setNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
      className={cn(
        "transition-transform duration-200 ease-out motion-reduce:transition-none",
        disabled ? "cursor-default" : "cursor-grab",
        isDragging && "invisible",
      )}
      style={shiftY == null ? undefined : { transform: `translate3d(0, ${shiftY}px, 0)` }}
    />
  )
}

function SlotDropFrame({
  position,
  disabled,
  highlight,
  className,
  onFrame,
  children,
}: {
  position: number
  disabled: boolean
  highlight: boolean
  className?: string
  onFrame: (position: number, element: HTMLDivElement | null) => void
  children: ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${SLOT_ID}${position}`, disabled })
  const ref = useCallback(
    (element: HTMLDivElement | null) => {
      setNodeRef(element)
      onFrame(position, element)
    },
    [setNodeRef, onFrame, position],
  )
  return (
    <FlowSlotFrame frameRef={ref} className={className} highlight={highlight && isOver}>
      {children}
    </FlowSlotFrame>
  )
}

export function FlowReorderableSlots({
  listKey,
  slots,
  inputs,
  nodes,
  projectId,
  frameFormat,
  frameRate,
  canEdit,
  locked,
  reorderLocked,
  emptyHint,
  className,
  leading,
  children,
  onDisconnect,
  onAttach,
  onCommit,
  onShownInputs,
}: {
  listKey: number
  slots: readonly FlowChainSlot[]
  inputs: readonly FlowNodeInput[]
  nodes: readonly FlowNodeRow[]
  projectId: number
  frameFormat: string
  frameRate?: number
  canEdit: boolean
  locked: boolean
  reorderLocked: boolean
  emptyHint: string
  className?: string
  leading?: ReactNode
  children?: ReactNode
  onDisconnect: (edgeId: number) => void
  onAttach: (slot: FlowChainSlot, batch: FlowAttachRequest) => Promise<FlowAttachOutcome | null>
  onCommit: (patch: FlowSlotMove) => Promise<void>
  onShownInputs?: (inputs: readonly FlowNodeInput[]) => void
}) {
  const { t } = useTranslation("flow")
  const [librarySlot, setLibrarySlot] = useState<FlowChainSlot | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [drag, setDrag] = useState<SlotDrag | null>(null)
  const [override, setOverride] = useState<Map<number, number> | null>(null)
  const [busyLocal, setBusyLocal] = useState(false)
  const [trackedKey, setTrackedKey] = useState(listKey)
  const dragRef = useRef<SlotDrag | null>(null)
  const suppressClick = useRef(false)
  const frames = useRef(new Map<number, HTMLDivElement>())
  const fileRef = useRef<HTMLInputElement>(null)
  const onShownInputsRef = useRef(onShownInputs)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  if (trackedKey !== listKey) {
    setTrackedKey(listKey)
    setDrag(null)
    setOverride(null)
    dragRef.current = null
  }
  onShownInputsRef.current = onShownInputs
  const shownInputs = useMemo(() => withSlotOverride(inputs, override), [inputs, override])
  const dragLocked = reorderLocked || busyLocal
  const controlsLocked = locked || busyLocal
  const untitled = t("node.untitled")
  const uploadLabel = t("chain.upload")
  const libraryLabel = t("chain.library")
  const disconnectLabel = t("chain.disconnect")

  useEffect(() => {
    onShownInputsRef.current?.(shownInputs)
  }, [shownInputs])

  const bindFrame = useCallback((position: number, element: HTMLDivElement | null) => {
    if (element) frames.current.set(position, element)
    else frames.current.delete(position)
  }, [])

  const releaseClick = () => {
    window.setTimeout(() => {
      suppressClick.current = false
    }, 0)
  }

  const libraryTargets = useMemo(
    () => [
      {
        id: "slot",
        label: librarySlot?.label ?? libraryLabel,
        accepts: librarySlot?.media === "video" ? (["video"] as const) : (["image"] as const),
      },
    ],
    [libraryLabel, librarySlot],
  )

  const attachToSlot = async (slot: FlowChainSlot, batch: FlowAttachRequest) => {
    if (dragLocked) return null
    setBusyLocal(true)
    try {
      return await onAttach(slot, batch)
    } finally {
      setBusyLocal(false)
    }
  }

  const finishLibrary = (source: ShotFrameSource, item: LibraryItem) => {
    const slot = librarySlot
    if (!slot) return
    const picks = flowAttachPicks([libraryPickResult(item, source)], slot.media)
    if (picks.length === 0) return
    void attachToSlot(slot, { picks }).then((outcome) => {
      if (outcome && outcome.failed === 0 && outcome.attached > 0) setLibrarySlot(null)
    })
  }

  const openFile = (slot: FlowChainSlot) => {
    const input = fileRef.current
    if (!input || dragLocked) return
    input.accept = slot.media === "video" ? REFERENCE_VIDEO_FILE_ACCEPT : REFERENCE_IMAGE_FILE_ACCEPT
    input.dataset.slot = String(slot.position)
    input.click()
  }

  const commit = async (patch: FlowSlotMove) => {
    if (dragLocked) {
      setOverride(null)
      return
    }
    setBusyLocal(true)
    try {
      await onCommit(patch)
      setOverride(null)
    } catch (error: unknown) {
      setOverride(null)
      toast.error(error instanceof Error ? error.message : t("errors.nodeFailed"))
    } finally {
      setBusyLocal(false)
    }
  }

  const onDragStart = (event: DragStartEvent) => {
    suppressClick.current = true
    const edgeId = dragSuffix(event.active.id, EDGE_ID)
    const moving = edgeId == null ? undefined : inputs.find((input) => input.id === edgeId)
    if (!moving || moving.slot_index == null) return
    const next = { edgeId: moving.id, from: moving.slot_index, to: null, dy: 0 }
    dragRef.current = next
    setDrag(next)
  }

  const onDragOver = (event: DragOverEvent) => {
    const current = dragRef.current
    if (!current) return
    const to = event.over ? dragSuffix(event.over.id, SLOT_ID) : null
    if (to == null || flowSlotDrop(slots, inputs, current.from, to) == null) {
      if (current.to == null) return
      const next = { ...current, to: null, dy: 0 }
      dragRef.current = next
      setDrag(next)
      return
    }
    const fromEl = frames.current.get(current.from)
    const toEl = frames.current.get(to)
    const dy = fromEl && toEl ? Math.round(toEl.getBoundingClientRect().top - fromEl.getBoundingClientRect().top) : 0
    if (current.to === to && current.dy === dy) return
    const next = { ...current, to, dy }
    dragRef.current = next
    setDrag(next)
  }

  const onDragEnd = (event: DragEndEvent) => {
    const current = dragRef.current
    dragRef.current = null
    setDrag(null)
    releaseClick()
    const to = event.over ? dragSuffix(event.over.id, SLOT_ID) : null
    if (!current || to == null || dragLocked) return
    const result = flowSlotDrop(slots, inputs, current.from, to)
    if (!result) return
    // Раскладка меняется в этом же commit: оверлей долетает в новый слот
    // и не проигрывает сдвиг заново, когда сервер ответит.
    setOverride(result.override)
    void commit(result.patch)
  }

  const onDragCancel = () => {
    dragRef.current = null
    setDrag(null)
    releaseClick()
  }

  const dragging = drag != null
  useEffect(() => {
    if (!dragging) return
    const previous = document.body.style.cursor
    document.body.style.cursor = "grabbing"
    return () => {
      document.body.style.cursor = previous
    }
  }, [dragging])

  const overlayFrom = drag?.from
  const overlayInput = drag ? inputs.find((input) => input.id === drag.edgeId) : undefined
  const overlaySlot = overlayFrom == null ? undefined : slots.find((slot) => slot.position === overlayFrom)
  const overlaySource = overlayInput ? nodes.find((row) => row.id === overlayInput.source_node_id) : undefined
  const overlay =
    drag && overlaySlot && overlaySource
      ? {
          name: flowSlotSourceLabel(overlaySource, untitled),
          mediaLabel: overlaySlot.media === "video" ? t("chain.video") : t("chain.image"),
          tile: flowSlotTile(overlaySource, overlaySlot.media),
          viewUrl: flowSlotViewUrl(overlaySource, overlaySlot.media),
        }
      : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className={className}>
        {leading}
        {slots.map((slot) => {
          const assigned = shownInputs.find((input) => input.slot_index === slot.position)
          const source = assigned ? nodes.find((row) => row.id === assigned.source_node_id) : undefined
          const visible = Boolean(assigned && source)
          const fromSlot = drag ? slots.find((item) => item.position === drag.from) : undefined
          const dropDisabled =
            dragLocked || drag == null || drag.from === slot.position || fromSlot?.media !== slot.media
          const shiftY = drag && drag.to === slot.position && visible ? -drag.dy : null
          const mediaLabel = slot.media === "video" ? t("chain.video") : t("chain.image")
          const name = flowSlotSourceLabel(source, untitled)
          const viewUrl = source ? flowSlotViewUrl(source, slot.media) : null
          return (
            <div key={slot.position} className={cn("relative flex flex-col gap-1.5", shiftY != null && "z-20")}>
              <FlowSlotHeading label={slot.label} media={slot.media} mediaLabel={mediaLabel} />
              <SlotDropFrame
                position={slot.position}
                disabled={dropDisabled}
                highlight={!visible}
                className={visible ? "p-1" : "gap-2 px-3"}
                onFrame={bindFrame}
              >
                {assigned && source ? (
                  <DraggableSlotCard
                    key={assigned.id}
                    edgeId={assigned.id}
                    disabled={dragLocked}
                    shiftY={shiftY}
                    name={name}
                    mediaLabel={mediaLabel}
                    tile={flowSlotTile(source, slot.media)}
                    viewUrl={viewUrl}
                    canDisconnect={canEdit}
                    disconnectLabel={disconnectLabel}
                    disconnectDisabled={controlsLocked}
                    onOpen={() => {
                      if (suppressClick.current || !viewUrl) return
                      setPreview({ url: viewUrl, video: slot.media === "video", title: name })
                    }}
                    onDisconnect={() => {
                      if (suppressClick.current) return
                      onDisconnect(assigned.id)
                    }}
                  />
                ) : (
                  <>
                    <FlowSlotEmptyHint>{emptyHint}</FlowSlotEmptyHint>
                    {canEdit ? (
                      <FlowSlotAttachButtons
                        disabled={dragLocked}
                        uploadLabel={uploadLabel}
                        libraryLabel={libraryLabel}
                        onUpload={() => openFile(slot)}
                        onLibrary={() => setLibrarySlot(slot)}
                      />
                    ) : null}
                  </>
                )}
              </SlotDropFrame>
            </div>
          )
        })}
        {children}
      </div>
      <DragOverlay className="pointer-events-none cursor-grabbing">
        {overlay ? (
          <FlowSlotCard
            className="h-full shadow-lg"
            name={overlay.name}
            mediaLabel={overlay.mediaLabel}
            tile={overlay.tile}
            viewUrl={overlay.viewUrl}
            canDisconnect={canEdit}
            disconnectLabel={disconnectLabel}
            disconnectDisabled
            onOpen={() => {}}
            onDisconnect={() => {}}
          />
        ) : null}
      </DragOverlay>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          const position = Number(event.currentTarget.dataset.slot)
          event.target.value = ""
          const slot = slots.find((item) => item.position === position)
          if (!file || !slot) return
          void attachToSlot(slot, { files: [file] })
        }}
      />
      <LibraryPickerDialog
        open={librarySlot != null}
        onOpenChange={(open) => {
          if (!open) setLibrarySlot(null)
        }}
        projectId={projectId}
        frameFormat={frameFormat}
        frameRate={frameRate}
        multiple={false}
        selectionLimit={1}
        subtitle={libraryLabel}
        targets={libraryTargets}
        defaultTargetId="slot"
        busyTargetId={busyLocal ? "slot" : null}
        onPick={(_targetId, source, item) => finishLibrary(source, item)}
        onPickMany={(_targetId, picked) => {
          const slot = librarySlot
          if (!slot) return
          const picks = flowAttachPicks(picked, slot.media)
          if (picks.length === 0) return
          void attachToSlot(slot, { picks }).then((outcome) => {
            if (outcome && outcome.failed === 0 && outcome.attached > 0) setLibrarySlot(null)
          })
        }}
      />
      <ImageLightboxDialog
        open={preview != null}
        onOpenChange={(open) => {
          if (!open) setPreview(null)
        }}
        url={preview && !preview.video ? preview.url : null}
        videoUrl={preview?.video ? preview.url : null}
        downloadUrl={preview?.url}
        title={preview?.title}
      />
    </DndContext>
  )
}
